import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { TableErrorPlaceholder } from './TableErrorPlaceholder';
import { generateErrorId, reportErrorToService } from '../../utils/errorReporting';

interface Props {
  children: ReactNode;
  tableId?: string;
  tableTitle?: string;
  height?: number | string;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
  timestamp: number;
}

export class TableErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      timestamp: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `TABLE_${generateErrorId()}`;
    const timestamp = Date.now();

    return {
      hasError: true,
      error,
      errorId,
      timestamp,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { tableId, tableTitle, onError } = this.props;

    console.group('🚨 TableErrorBoundary caught an error');
    console.error('Table ID:', tableId || 'unknown');
    console.error('Table Title:', tableTitle || 'unknown');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Error ID:', this.state.errorId);
    console.error('Timestamp:', new Date(this.state.timestamp).toISOString());
    console.groupEnd();

    this.setState({ errorInfo });

    onError?.(error, errorInfo);

    if (import.meta.env.PROD) {
      reportErrorToService({
        errorId: this.state.errorId,
        timestamp: this.state.timestamp,
        error,
        errorInfo,
        context: {
          tableId: this.props.tableId,
          tableTitle: this.props.tableTitle,
        },
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      timestamp: 0,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }


      return (
        <TableErrorPlaceholder
          error={this.state.error}
          onRetry={this.handleReset}
          tableTitle={this.props.tableTitle}
          height={this.props.height}
          errorId={this.state.errorId}
        />
      );
    }

    return this.props.children;
  }
}


